// PaymentModal.jsx
import React, { useState } from 'react';
import Dialog from './components/ui/Dialog';
import { Button } from './components/ui/button';
import { useStripe, useElements, PaymentElement, Elements } from '@stripe/react-stripe-js';

function PaymentModal({ idToken, onClose, onSuccess, userEmail, stripePromise }) {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [loadingSecret, setLoadingSecret] = useState(false);
  const [error, setError] = useState('');
  // const [processing, setProcessing] = useState(false);

  // 1) Fetch clientSecret when user selects plan
  const fetchClientSecret = async (planType) => {
    setLoadingSecret(true);
    setError('');
    try {
      const response = await fetch(
        'https://o5s6iz75t2.execute-api.us-east-1.amazonaws.com/create-payment',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}`, 'X-Authorization-Provider': 'google' },
          body: JSON.stringify({ email: userEmail, planType }),
        }
      );
      if (!response.ok) throw new Error(await response.text());

      const data = await response.json();
      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
      } else {
        throw new Error('No clientSecret returned by server.');
      }
    } catch (err) {
      console.error('Error fetching clientSecret:', err);
      setError(err.message);
    } finally {
      setLoadingSecret(false);
    }
  };

  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
    fetchClientSecret(plan);
  };

  // 2) If we have a clientSecret, render PaymentElement inside <Elements>
  return (
    <Dialog isOpen onClose={onClose} title={clientSecret ? 'Complete your payment' : 'Select your plan'}>

        {/* If no plan selected yet */}
        {!selectedPlan && !clientSecret && (
          <>
            <div className="rounded-xl border border-solid border-slate-200 bg-slate-50 p-4">
              <h3>Yearly Subscription</h3>
              <p>$10 per year. Auto-renews annually.</p>
              <Button variant="brand" size="action" className="m-0 mt-3 w-full" onClick={() => handlePlanSelection('yearly')}>Choose Yearly</Button>
            </div>
          </>
        )}

        {/* Show a loading message if we've chosen a plan but are still fetching clientSecret */}
        {selectedPlan && !clientSecret && loadingSecret && (
          <p role="status">Loading payment details...</p>
        )}

        {/* If we have clientSecret, wrap PaymentElement in <Elements> */}
        {clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
              clientSecret={clientSecret}
              onClose={onClose}
              onSuccess={onSuccess}
              selectedPlan={selectedPlan}
            />
          </Elements>
        )}

        {/* Show error if any */}
        {error && <p role="alert" className="rounded-xl bg-red-50 p-3 text-red-800">{error}</p>}
    </Dialog>
  );
}

// 3) PaymentForm: separate child for PaymentElement so we can keep logic clean
function PaymentForm({ clientSecret, onClose, onSuccess, selectedPlan }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError('');

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message);
        setProcessing(false);
        return;
      }

      const { error: paymentError } = await stripe.confirmPayment({
        elements,
        clientSecret,
        redirect: 'if_required',
      });

      if (paymentError) {
        setError(paymentError.message);
        setProcessing(false);
        return;
      }

      onSuccess(); 
    } catch (err) {
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2>Yearly Subscription</h2>
      <PaymentElement />
      {error && <p role="alert" className="rounded-xl bg-red-50 p-3 text-red-800">{error}</p>}
      <Button variant="brand" size="action" className="m-0 mt-4 w-full" type="submit" disabled={processing}>
        {processing ? 'Processing...' : 'Pay Now'}
      </Button>
    </form>
  );
}

export default PaymentModal;
